import { chronicleIsRecordingAtom, chronicleLiveOfflineAtom } from '@/atoms/chronicle';
import { activeSessionIdAtom } from '@/atoms/liveTranscriptionAtom';
import { offlineCaptureRunningAtom } from '@/atoms/offline-recording';
import { useQuerySessionByIdAuthenticated } from '@/hooks/query/useQuerySessionById';
import { logger } from '@/utils/logger/logger';
import { ConsultNoteStatus, RecordingStatus } from '@heidi/schema/ml-scribe';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';

export interface ActiveRecordingGuardReconcilerParams {
  /**
   * `timerModel.isRunning`. Passed in rather than read here, per the Rematch
   * containment ratchet (`@heidi/rules/no-new-rematch-coupling`) — same shape as
   * `useIsClinicalRecordingActive`. The caller is `_app.tsx`, which is already
   * store-coupled.
   */
  isTimerRunning: boolean;
  /** `timerModel.seconds`, reported alongside the release for triage. */
  timerSeconds: number;
  /**
   * Op-killswitch from `_app.tsx` (`op-scribe-disable-recording-guard-reconciler`).
   * When false the hook is a no-op so a fleet misfire can be disabled without
   * rollback. Default true keeps the reconciler armed.
   */
  enabled?: boolean;
}

/**
 * Releases `activeSessionIdAtom` when the session it points at is
 * demonstrably finished — a LEVEL-triggered backstop for the EDGE-triggered
 * clears that own the atom on every normal path.
 *
 * # Why a reconciler and not a fifth branch
 *
 * The atom is cleared only when a specific transition is caught, and at least
 * four sites between "the device stops or drops" and `setActiveSessionId(null)`
 * can decline silently:
 *
 *   1. `processRecordingDrop` — a `connected` payload that lands before the
 *      post-connect `APP&STE` resolves keeps holding.
 *   2. `processRecordingDrop` — a resolved `connected` still reporting
 *      `recordingPreExisting` keeps holding.
 *   3. `useChronicleSessionControl`'s device-stop detector — its suppression
 *      predicate no-ops the transition.
 *   4. `useChronicleSessionControl` again — `prevRecordingRef.current` is
 *      advanced BEFORE the `isHandlingRef.current || isStarting` early return,
 *      so a transition that arrives while either is set is consumed and lost
 *      for good.
 *
 * None of those logs anything. Observed in production: a Remote recording
 * ended by BLE disconnect, bulk-synced, transcribed and produced a note with
 * four clinical tasks, and the guard was still armed on that session three
 * hours later. Patching a fifth branch does not fix an architecture that
 * depends on catching one edge.
 *
 * # Direction
 *
 * ONE-WAY: this may only ever CLEAR the atom, never set it. It therefore cannot
 * cause a navigation to be blocked that is not blocked today — the worst
 * failure mode is releasing a guard early, which the interlocks below exist to
 * make unreachable.
 *
 * # Mobile parity
 *
 * Android does the same thing with the same field: on every session-detail
 * fetch, `SessionDetailRepositoryImpl` clears a stale local
 * `bleDisconnectionState` when `consult_note.status == COMPLETED`, with no
 * debounce. It never keys off `session_status` (untyped there, and its own two
 * spec contracts disagree on the value domain), and it never treats status
 * alone as terminal — `NoteStreamRecoveryHelper` and
 * `TelehealthServerNoteAwaitPolicy` both require a non-blank `result`. Desktop
 * already agrees: `useCreateButtonV2` reads the same pair.
 *
 * `GENERATING` is a real wire value that is absent from every platform's typed
 * enum. Releasing only on an explicit positive `COMPLETED` makes any unmodelled
 * value safe by construction.
 */
export const useActiveRecordingGuardReconciler = ({
  isTimerRunning,
  timerSeconds,
  enabled = true,
}: ActiveRecordingGuardReconcilerParams) => {
  const activeSessionId = useAtomValue(activeSessionIdAtom);
  const setActiveSessionId = useSetAtom(activeSessionIdAtom);
  const isOfflineCaptureRunning = useAtomValue(offlineCaptureRunningAtom);
  const isChronicleRecording = useAtomValue(chronicleIsRecordingAtom);
  const liveOffline = useAtomValue(chronicleLiveOfflineAtom);

  // Suspicious leftover: guard armed, timer stopped, no offline capture, and
  // Chronicle is not mid-take. Pause and BLE hold keep `chronicleSessionIdAtom`
  // (and thus `chronicleIsRecordingAtom`) set while the timer is stopped — that
  // is the "device still in a take" signal leftover cases do not still hold.
  // Do NOT interlock on `chronicleLiveOfflineAtom`: a leftover hold with a
  // finished note would then never release.
  const isCandidate =
    enabled &&
    activeSessionId !== null &&
    !isTimerRunning &&
    !isOfflineCaptureRunning &&
    !isChronicleRecording;

  const consultNote = useQuerySessionByIdAuthenticated({
    sessionId: isCandidate ? activeSessionId : '',
    select: (data) => ({
      status: data?.consult_note?.status,
      hasResult: Boolean(data?.consult_note?.result?.trim()),
      recordingStatus: data?.recording_status,
    }),
  }).data;

  useEffect(() => {
    if (!isCandidate) return;
    if (consultNote?.status !== ConsultNoteStatus.COMPLETED) return;
    if (!consultNote.hasResult) return;
    // Optimistic-start window: `onMutate` arms `activeSessionIdAtom` (and the
    // cache `recording_status`) before the timer ticks / Chronicle claims the
    // session. Addendum / record-more on an already-COMPLETED note would
    // otherwise look identical to a leftover and drop the guard mid-take.
    // Hours-later leftovers see IDLE after a refetch (RECORDING is never
    // persisted), so this does not block the release path.
    if (
      consultNote.recordingStatus === RecordingStatus.RECORDING ||
      consultNote.recordingStatus === RecordingStatus.PAUSED
    ) {
      return;
    }

    // Every term, so the next occurrence names the branch that dropped the edge
    // instead of costing another investigation. Paired with the fast-path
    // canary in `use-active-recording-guard`, this is what makes the fix
    // measurable in production.
    logger.warn('RECORDING_GUARD_RECONCILED', {
      active_session_id: activeSessionId,
      timer_seconds: timerSeconds,
      consult_note_status: consultNote.status,
      had_live_offline: liveOffline !== null,
    });

    setActiveSessionId(null);
  }, [
    isCandidate,
    consultNote,
    activeSessionId,
    timerSeconds,
    liveOffline,
    setActiveSessionId,
  ]);
};
